import * as PIXI from 'pixi.js';
import { Entity } from '../core/EntityManager';
import { Vector2 } from '../core/Vector2';
import { PLAYER_SPEED, PLAYER_COLLIDER_RADIUS } from '@hamsters/game-config';
import type { InputSystem } from '../systems/InputSystem';
import { getSprite } from '../content/SpriteCache';

export class Player extends Entity {
  private hamsterSprite: PIXI.Sprite | null = null;
  private gunSprite: PIXI.Sprite | null = null;
  private direction = new Vector2(1, 0);
  private hasGun = false;

  constructor() {
    super();
    this.radius = PLAYER_COLLIDER_RADIUS;

    // Load hamster face from cache
    const hamsterTexture = getSprite('hamster');
    if (hamsterTexture) {
      this.hamsterSprite = new PIXI.Sprite(hamsterTexture);
      this.hamsterSprite.anchor.set(0.5);
      this.hamsterSprite.width = PLAYER_COLLIDER_RADIUS * 2.5;
      this.hamsterSprite.height = PLAYER_COLLIDER_RADIUS * 2.5;
      this.sprite.addChild(this.hamsterSprite);
    } else {
      this.drawFallback();
    }

    // Load pistol from cache
    const pistolTexture = getSprite('pistol');
    if (pistolTexture) {
      this.gunSprite = new PIXI.Sprite(pistolTexture);
      this.gunSprite.anchor.set(0, 0.5);
      this.gunSprite.width = 40;
      this.gunSprite.height = 20;
      this.gunSprite.x = 15;
      this.gunSprite.visible = false;
      this.sprite.addChild(this.gunSprite);
    }
  }

  private drawFallback(): void {
    const g = new PIXI.Graphics();
    g.circle(0, 0, PLAYER_COLLIDER_RADIUS);
    g.fill(0xffcc66);
    g.circle(-5, -4, 3);
    g.fill(0x000000);
    g.circle(5, -4, 3);
    g.fill(0x000000);
    this.sprite.addChild(g);
  }

  setWeapon(visible: boolean): void {
    this.hasGun = visible;
    if (this.gunSprite) {
      this.gunSprite.visible = visible;
    }
  }

  update(_dt: number, _tick: number, input?: InputSystem): void {
    if (!input) return;

    const state = input.getState();
    const move = state.move;

    if (move.lengthSq() > 0) {
      move.normalize();
      this.velocity.set(move.x * PLAYER_SPEED, move.y * PLAYER_SPEED);
      this.direction.copy(move);

      if (!this.hasGun) {
        this.sprite.scale.x = move.x >= 0 ? 1 : -1;
      }
    } else {
      this.velocity.set(0, 0);
    }

    this.position.x += this.velocity.x * _dt;
    this.position.y += this.velocity.y * _dt;

    this.sprite.x = this.position.x;
    this.sprite.y = this.position.y;

    if (this.hasGun && this.gunSprite) {
      this.gunSprite.rotation = Math.atan2(this.direction.y, this.direction.x);
    }
  }

  getDirection(): Vector2 {
    return this.direction.clone();
  }

  getSpeed(): number {
    return this.velocity.length();
  }
}
